import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getRegionMeta } from '../utils/region';
type LiveOrder = {
  material: string;
  quantity: number;
  city: string;
  createdAt: Date;
};
const ORDER_TEMPLATES = [{
  material: '5/8" Minus',
  quantity: 12,
  fallbackCity: 'Tacoma'
}, {
  material: '3-Way Topsoil',
  quantity: 15,
  fallbackCity: 'Kent'
}, {
  material: 'Drain Rock',
  quantity: 10,
  fallbackCity: 'Puyallup'
}, {
  material: 'Medium Bark',
  quantity: 14,
  fallbackCity: 'Bellevue'
}, {
  material: 'Pea Gravel',
  quantity: 8,
  fallbackCity: 'Auburn'
}, {
  material: '5/8" Clear',
  quantity: 11,
  fallbackCity: 'Renton'
}] as const;
type LiveOrderTickerProps = {
  isDispatchActive?: boolean;
  opensIn?: string;
  deliveryMessage?: string;
};
export function LiveOrderTicker({
  isDispatchActive = true,
  opensIn = '',
  deliveryMessage = ''
}: LiveOrderTickerProps) {
  const shouldReduceMotion = useReducedMotion();
  const [now, setNow] = useState(() => new Date());
  const [closedMessageIndex, setClosedMessageIndex] = useState(0);
  const [activeOrderIndex, setActiveOrderIndex] = useState(0);
  const orders = useMemo<LiveOrder[]>(() => {
    const cities = getRegionMeta().cities;
    const created = new Date();
    return ORDER_TEMPLATES.map((order, index) => {
      const createdAt = new Date(created);
      createdAt.setMinutes(createdAt.getMinutes() - (index * 19 + 6));
      return {
        material: order.material,
        quantity: order.quantity,
        city: cities[index % cities.length] || order.fallbackCity,
        createdAt
      };
    });
  }, []);
  useEffect(() => {
    // When dispatch closes, lock the relative time in place so this reads as
    // the last completed feed rather than implying orders are still arriving.
    setNow(new Date());
    if (!isDispatchActive) return;
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive]);
  useEffect(() => {
    if (isDispatchActive || shouldReduceMotion) {
      setClosedMessageIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setClosedMessageIndex(index => (index + 1) % 2);
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive, shouldReduceMotion]);
  useEffect(() => {
    if (!isDispatchActive || shouldReduceMotion) {
      setActiveOrderIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setActiveOrderIndex(index => (index + 1) % orders.length);
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive, orders.length, shouldReduceMotion]);
  if (!isDispatchActive) {
    const availabilityMessage = 'Dispatch closed · Order online 24/7';
    const nextDeliveryMessage = deliveryMessage.includes('Monday') ? 'Monday delivery available' : 'Next day delivery available';
    const closedMessages = [availabilityMessage, nextDeliveryMessage];
    const activeMessage = closedMessages[closedMessageIndex];
    return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label={`${availabilityMessage}. ${nextDeliveryMessage}.`} data-id="element-1128" data-mp="28duebh96oon0">
        <span className="mr-2 flex h-3 shrink-0 items-end gap-0.5" aria-hidden="true" data-id="element-1129" data-mp="2d5vvnhl7l5jw">
          <span className="h-1.5 w-0.5 rounded-full bg-white/30" data-id="element-1130" data-mp="2i34954vewlet" />
          <span className="h-3 w-0.5 rounded-full bg-white/30" data-id="element-1131" data-mp="1leoewvhcxnvd" />
          <span className="h-2 w-0.5 rounded-full bg-white/30" data-id="element-1132" data-mp="1lgj26gg3ps2m" />
        </span>
        <div className="min-w-0 overflow-hidden" data-id="element-1133" data-mp="1pg1nhdbu455z">
          <AnimatePresence initial={false} mode="wait" data-id="element-1134" data-mp="1uebk7sd7jdtp">
            <motion.p key={activeMessage} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={shouldReduceMotion ? undefined : {
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="truncate whitespace-nowrap font-medium tracking-wide text-white/70 text-[12px]" data-id="element-1135" data-mp="1ugxq7nkg9l9t">
              {activeMessage}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>;
  }
  const activeOrder = orders[activeOrderIndex];
  const activeOrderMessage = `${activeOrder.quantity} yd ${activeOrder.material} → ${activeOrder.city} · ${formatRelativeTime(activeOrder.createdAt, now)}`;
  return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label={`Recent order: ${activeOrderMessage}`} data-id="element-1136" data-mp="rkp9l53b2wmh">
      <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1137" data-mp="16hevhhk5b4h2">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#00b774]" data-id="element-1138" data-mp="11jn4qqv8loeg">Live</span>
        <span className="flex h-3 items-end gap-0.5" aria-hidden="true" data-id="element-1139" data-mp="1fajmfmnmkl6q">
          {[0, 1, 2].map(index => <motion.span key={index} className="w-0.5 rounded-full bg-[#00b774]" animate={{
          height: ['35%', '100%', '35%']
        }} transition={{
          duration: 1.15,
          repeat: Infinity,
          delay: index * 0.18,
          ease: 'easeInOut'
        }} data-id="element-1140" data-mp="1bbj2u1xbpeio" />)}
        </span>
      </div>
      <span className="mr-2 shrink-0 text-white/30" aria-hidden="true" data-id="element-1141" data-mp="23x93p603l6f7">•</span>
      <div className="min-w-0 overflow-hidden" data-id="element-1142" data-mp="28urhpasz2bu2">
        <AnimatePresence initial={false} mode="wait" data-id="element-1143" data-mp="28tgbz5fyuu0b">
          <motion.p key={`${activeOrder.material}-${activeOrder.city}`} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={shouldReduceMotion ? undefined : {
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="truncate whitespace-nowrap text-left text-[11px] font-medium text-white/50 sm:text-xs" data-id="element-1144" data-mp="1txqmgxbknyqu">
            <span className="text-white/70" data-id="element-1145" data-mp="1g86mu0dafhlr">{activeOrder.quantity} yd {activeOrder.material}</span>
            <span className="mx-1.5 text-[#00b774]" data-id="element-1146" data-mp="128f7nwb1bv66">→</span>
            <span data-id="element-1147" data-mp="168xvzgdpyj3m">{activeOrder.city}</span>
            <span className="mx-1.5 text-white/35" data-id="element-1148" data-mp="167nyoteoxey1">·</span>
            <span className="text-white/35" data-id="element-1149" data-mp="38vcs192q256x">{formatRelativeTime(activeOrder.createdAt, now)}</span>
          </motion.p>
        </AnimatePresence>
      </div>
    </div>;
}
function formatRelativeTime(createdAt: Date, now: Date): string {
  const minutes = Math.max(1, Math.round((now.getTime() - createdAt.getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)} hr ago`;
}